import React from 'react';
import Modal from './modal';
import { Button } from './button';
import { Lock, Crown, ArrowRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

interface UpgradeRequiredModalProps {
  isOpen: boolean;
  onClose: () => void;
  featureName: string;
  requiredPlan: 'Premium' | 'Premium+';
}

const UpgradeRequiredModal: React.FC<UpgradeRequiredModalProps> = ({
  isOpen,
  onClose,
  featureName,
  requiredPlan
}) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const currentLang = i18n.language || 'fr';

  const handleUpgrade = () => {
    navigate(`/${currentLang}/provider/billing`);
    onClose();
  };

  const footer = (
    <>
      <Button
        type="button"
        onClick={onClose}
        variant="outline"
        className="border-gray-300 dark:border-gray-700"
      >
        {t('upgradeModal.cancel', 'Fermer')}
      </Button>
      <Button
        type="button"
        onClick={handleUpgrade}
        className="bg-amber-500 hover:bg-amber-600 text-white"
      >
        <Crown size={16} className="mr-2" />
        {t('upgradeModal.upgrade', 'Passer à Premium')}
        <ArrowRight size={16} className="ml-2" />
      </Button>
    </>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('upgradeModal.title', 'Fonctionnalité Premium')}
      footer={footer}
      size="md"
    >
      <div className="flex flex-col items-center text-center space-y-4">
        <div className="w-16 h-16 bg-amber-100 dark:bg-amber-900/30 rounded-full flex items-center justify-center">
          <Lock size={28} className="text-amber-500 dark:text-amber-400" />
        </div>

        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
            {featureName}
          </h3>
          <p className="text-gray-600 dark:text-gray-400">
            {t('upgradeModal.description', 'Cette fonctionnalité est disponible avec le plan')} <span className="font-semibold text-amber-600 dark:text-amber-400">{requiredPlan}</span>
          </p>
        </div>

        <div className="w-full bg-gray-50 dark:bg-gray-800 rounded-lg p-4 mt-4">
          <p className="text-sm text-gray-700 dark:text-gray-300 mb-3">
            {t('upgradeModal.benefits', 'Avec le plan Premium, débloquez :')}
          </p>
          <ul className="text-sm text-left space-y-2 text-gray-600 dark:text-gray-400">
            {requiredPlan === 'Premium' && (
              <>
                <li className="flex items-start">
                  <Crown size={16} className="text-amber-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span>{t('upgradeModal.benefit1', 'Système de réservation avancé')}</span>
                </li>
                <li className="flex items-start">
                  <Crown size={16} className="text-amber-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span>{t('upgradeModal.benefit2', 'Gestion des avis clients')}</span>
                </li>
                <li className="flex items-start">
                  <Crown size={16} className="text-amber-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span>{t('upgradeModal.benefit3', 'Jusqu\'à 2 collaborateurs')}</span>
                </li>
                <li className="flex items-start">
                  <Crown size={16} className="text-amber-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span>{t('upgradeModal.benefit4', '3 codes promo actifs')}</span>
                </li>
              </>
            )}
            {requiredPlan === 'Premium+' && (
              <>
                <li className="flex items-start">
                  <Crown size={16} className="text-amber-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span>{t('upgradeModal.benefit5', 'Toutes les fonctionnalités Premium')}</span>
                </li>
                <li className="flex items-start">
                  <Crown size={16} className="text-amber-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span>{t('upgradeModal.benefit6', 'Jusqu\'à 2 entreprises')}</span>
                </li>
                <li className="flex items-start">
                  <Crown size={16} className="text-amber-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span>{t('upgradeModal.benefit7', 'Jusqu\'à 5 collaborateurs')}</span>
                </li>
                <li className="flex items-start">
                  <Crown size={16} className="text-amber-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span>{t('upgradeModal.benefit8', 'Codes promo illimités')}</span>
                </li>
              </>
            )}
          </ul>
        </div>
      </div>
    </Modal>
  );
};

export default UpgradeRequiredModal;
